import { FC, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, Stack } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { categoryLanguages } from '../../helpers/categoryLanguageHelpers'
import { FeatureChoiceValues, newChoiceValues } from '../helpers/featureHelpers'

type ChoiceEditDialogProps = {
  open: boolean
  // Without a choice the dialog adds one.
  choice?: FeatureChoiceValues
  onClose: () => void
  onSave: (choice: FeatureChoiceValues) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const ChoiceEditDialog: FC<ChoiceEditDialogProps> = ({ open, choice, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useForm<FeatureChoiceValues>({ mode: 'onChange', defaultValues: choice ?? newChoiceValues() })
  const english = useWatch({ control, name: 'labels' })?.en

  useEffect(() => {
    if (open) reset(choice ?? newChoiceValues())
  }, [open, choice, reset])

  const submit = handleSubmit((values) => onSave(values))

  return (
    <InternalFormDialog
      open={open}
      title={choice ? t('internal-analysts:categories_choice_edit') : t('internal-analysts:categories_choice_add')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!english?.trim()}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="labels"
          control={control}
          rules={{ validate: (labels) => !!labels.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-analysts:categories_choice_name')}
              dialogTitle={t('internal-analysts:categories_choice_name')}
              selectedLanguages={categoryLanguages}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={t('internal-analysts:categories_choice_active')}
              />
            )}
          />
          <Controller
            name="showInLists"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={t('internal-analysts:categories_choice_show_in_lists')}
              />
            )}
          />
        </Stack>
      </Stack>
    </InternalFormDialog>
  )
}
